import "./styles.css";
import { useEffect, useState } from "react";
import useLangStore from "@/hooks/useLangStore";

export interface GalleryPopupDetails {
    images: string[];
    alt?: string;
}

interface Props {
    onClose: () => void;
    details?: GalleryPopupDetails;
}

export default function GalleryPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const isRtl = lang === "ar";

    const images = details?.images ?? [];
    const alt = details?.alt ?? "";
    const [selected, setSelected] = useState(0);

    const prev = () => setSelected((i) => Math.max(0, i - 1));
    const next = () => setSelected((i) => Math.min(images.length - 1, i + 1));

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
                return;
            }
            if (e.key === "ArrowLeft") {
                if (isRtl) next();
                else prev();
            }
            if (e.key === "ArrowRight") {
                if (isRtl) prev();
                else next();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [selected, images.length, isRtl]);

    const isFirst = selected === 0;
    const isLast = selected === images.length - 1;
    const multi = images.length > 1;

    return (
        <div className="gallery-popup">
            {/* Close */}
            <button type="button" className="gallery-popup-close" onClick={onClose} aria-label="Close">
                ×
            </button>

            {/* Main image */}
            <div className="gallery-popup-main">
                {multi && (
                    <button
                        type="button"
                        className={`gallery-popup-arrow gallery-popup-arrow--prev${isFirst ? " gallery-popup-arrow--disabled" : ""}`}
                        onClick={prev}
                        disabled={isFirst}
                        aria-label="Previous"
                    >
                        <img src="/icons/arrow-dark-icon.svg" alt="" width={14} height={14} />
                    </button>
                )}

                <div className="gallery-popup-img-wrap">
                    <img key={selected} src={images[selected]} alt={`${alt} ${selected + 1}`} className="gallery-popup-img" />
                </div>

                {multi && (
                    <button
                        type="button"
                        className={`gallery-popup-arrow gallery-popup-arrow--next${isLast ? " gallery-popup-arrow--disabled" : ""}`}
                        onClick={next}
                        disabled={isLast}
                        aria-label="Next"
                    >
                        <img src="/icons/arrow-dark-icon.svg" alt="" width={14} height={14} />
                    </button>
                )}
            </div>

            {/* Counter + thumbnails */}
            {multi && (
                <>
                    <p className="gallery-popup-counter">
                        {selected + 1} / {images.length}
                    </p>
                    <div className="gallery-popup-thumbs">
                        {images.map((src, i) => (
                            <button
                                key={i}
                                type="button"
                                className={`gallery-popup-thumb${i === selected ? " gallery-popup-thumb--active" : ""}`}
                                onClick={() => setSelected(i)}
                                aria-label={`Image ${i + 1}`}
                            >
                                <img src={src} alt={`${alt} ${i + 1}`} />
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
